import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useCookies } from "react-cookie";

import Icons from "../components/common/Icons";
import Privacy from "../components/common/Modals/Privacy";
import { ArrowFillButton } from "../components/Dashboard/MissionPage/ArrowFillButton";
import axiosInstance from "../utils/axiosInstance";
import { SuccessToast } from "../utils/customToast";
import { validateFormField } from "../utils/validateFormField";

// ============================================
// COSMIC STAR FIELD - Matching Login page
// ============================================
const CosmicStarField = ({ isLaunching }) => {
  const stars = useMemo(() => {
    return Array.from({ length: 100 }, (_, i) => {
      const x = Math.random() * 100;
      const y = Math.random() * 100;

      const sizeRandom = Math.random();
      let size;
      if (sizeRandom > 0.92) {
        size = 3 + Math.random() * 1.5;
      } else if (sizeRandom > 0.7) {
        size = 2 + Math.random() * 1;
      } else {
        size = 1 + Math.random() * 1;
      }

      // F02 calm sky (as Login): fewer, slower breathers, same random draws.
      const doesTwinkle = Math.random() > 0.86;
      const twinkleDuration = 6 + Math.random() * 4;
      const twinkleDelay = Math.random() * 5;
      const baseOpacity = 0.3 + Math.random() * 0.5;

      const colorRandom = Math.random();
      let color;
      if (colorRandom > 0.9) {
        color = "rgba(171, 235, 247, 1)";
      } else if (colorRandom > 0.82) {
        color = "rgba(218, 236, 55, 0.9)";
      } else if (colorRandom > 0.65) {
        color = "rgba(210, 218, 239, 1)";
      } else {
        color = "rgba(255, 255, 255, 1)";
      }

      return {
        id: i,
        x,
        y,
        size,
        doesTwinkle,
        twinkleDuration,
        twinkleDelay,
        baseOpacity,
        color,
      };
    });
  }, []);

  return (
    <div className="xe-f02-sky absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            backgroundColor: star.color,
            opacity: star.baseOpacity,
            boxShadow:
              star.size > 2
                ? `0 0 ${star.size * 4}px ${star.color.replace("1)", "0.6)")}`
                : `0 0 ${star.size * 2}px ${star.color.replace("1)", "0.4)")}`,
            animation: isLaunching
              ? `warpSpeed 800ms ease-in forwards ${Math.random() * 200}ms`
              : star.doesTwinkle
                ? `registerStarTwinkle ${star.twinkleDuration}s ease-in-out ${star.twinkleDelay}s infinite`
                : "none",
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// NEBULA EFFECTS
// ============================================
const NebulaEffects = () => (
  <div className="xe-f02-sky absolute inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute rounded-full"
      style={{
        width: "700px",
        height: "700px",
        top: "-25%",
        right: "-20%",
        background:
          "radial-gradient(ellipse at center, rgba(171, 235, 247, 0.1) 0%, rgba(25, 42, 77, 0.05) 40%, transparent 70%)",
        filter: "blur(60px)",
        animation: "nebulaDrift 25s ease-in-out infinite",
      }}
    />
    <div
      className="absolute rounded-full"
      style={{
        width: "600px",
        height: "600px",
        bottom: "-20%",
        left: "-20%",
        background:
          "radial-gradient(ellipse at center, rgba(255, 90, 95, 0.08) 0%, rgba(32, 53, 96, 0.04) 40%, transparent 70%)",
        filter: "blur(50px)",
        animation: "nebulaDrift2 30s ease-in-out infinite",
      }}
    />
    <div
      className="absolute rounded-full"
      style={{
        width: "500px",
        height: "500px",
        top: "40%",
        left: "40%",
        background:
          "radial-gradient(ellipse at center, rgba(218, 236, 55, 0.04) 0%, transparent 60%)",
        filter: "blur(80px)",
        animation: "nebulaDrift 35s ease-in-out infinite reverse",
      }}
    />
  </div>
);

// ============================================
// SOLAR SYSTEM PROGRESS INDICATOR
// ============================================
const SolarSystemProgress = ({ currentStep, totalSteps }) => {
  const planets = useMemo(() => {
    const planetColors = [
      { main: "#ABEBF7", glow: "rgba(171, 235, 247, 0.5)" }, // Email - Charlotte blue
      { main: "#FF5A5F", glow: "rgba(255, 90, 95, 0.5)" }, // Name - Persimmon
      { main: "#DAEC37", glow: "rgba(218, 236, 55, 0.5)" }, // Password - Pear
      { main: "#9B7BFF", glow: "rgba(155, 123, 255, 0.5)" }, // DOB - Purple
      { main: "#FF9F43", glow: "rgba(255, 159, 67, 0.5)" }, // Gender - Orange
      { main: "#00D9FF", glow: "rgba(0, 217, 255, 0.5)" }, // Education - Cyan
      { main: "#FF6B9D", glow: "rgba(255, 107, 157, 0.5)" }, // School - Pink
    ];

    return Array.from({ length: totalSteps }, (_, i) => ({
      id: i + 1,
      orbitRadius: 60 + i * 28,
      size: i === currentStep - 1 ? 16 : 10,
      color: planetColors[i],
      orbitDuration: 20 + i * 8,
      startAngle: (i * 360) / totalSteps,
    }));
  }, [currentStep, totalSteps]);

  return (
    <div className="xe-f02-sky relative w-[400px] h-[400px] flex items-center justify-center">
      {/* Central sun */}
      <div
        className="absolute rounded-full z-10"
        style={{
          width: "50px",
          height: "50px",
          background:
            "radial-gradient(circle at 30% 30%, #FFF5E0 0%, #FFD700 40%, #FF8C00 100%)",
          boxShadow:
            "0 0 40px rgba(255, 215, 0, 0.6), 0 0 80px rgba(255, 140, 0, 0.4), 0 0 120px rgba(255, 140, 0, 0.2)",
        }}
      >
        {/* Sun corona */}
        <div
          className="absolute inset-[-8px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)",
          }}
        />
      </div>

      {/* Orbit paths */}
      {planets.map((planet) => (
        <div
          key={`orbit-${planet.id}`}
          className="absolute rounded-full border"
          style={{
            width: `${planet.orbitRadius * 2}px`,
            height: `${planet.orbitRadius * 2}px`,
            borderColor:
              planet.id <= currentStep
                ? "rgba(171, 235, 247, 0.2)"
                : "rgba(255, 255, 255, 0.05)",
            borderStyle: planet.id <= currentStep ? "solid" : "dashed",
            transition: "all 0.5s ease",
          }}
        />
      ))}

      {/* Planets */}
      {planets.map((planet) => {
        const isActive = planet.id === currentStep;
        const isCompleted = planet.id < currentStep;
        const isFuture = planet.id > currentStep;

        return (
          <div
            key={`planet-${planet.id}`}
            className="absolute"
            style={{
              width: `${planet.orbitRadius * 2}px`,
              height: `${planet.orbitRadius * 2}px`,
              animation: isActive
                ? `orbit ${planet.orbitDuration}s linear infinite`
                : "none",
              transform: isActive ? "none" : `rotate(${planet.startAngle}deg)`,
            }}
          >
            <div
              className="absolute rounded-full transition-all duration-500"
              style={{
                width: `${planet.size}px`,
                height: `${planet.size}px`,
                left: "50%",
                top: "0",
                transform: "translateX(-50%)",
                background: isCompleted
                  ? `radial-gradient(circle at 30% 30%, ${planet.color.main} 0%, ${planet.color.main}80 100%)`
                  : isFuture
                    ? "rgba(255, 255, 255, 0.1)"
                    : `radial-gradient(circle at 30% 30%, ${planet.color.main} 0%, ${planet.color.main}80 100%)`,
                boxShadow: isActive
                  ? `0 0 20px ${planet.color.glow}, 0 0 40px ${planet.color.glow}`
                  : isCompleted
                    ? `0 0 10px ${planet.color.glow}`
                    : "none",
                border: isFuture
                  ? "1px dashed rgba(255, 255, 255, 0.2)"
                  : "none",
              }}
            >
              {/* Planet ring effect for active planet */}
              {isActive && (
                <div
                  className="absolute rounded-full"
                  style={{
                    width: `${planet.size + 12}px`,
                    height: `${planet.size + 12}px`,
                    left: "-6px",
                    top: "-6px",
                    border: `2px solid ${planet.color.main}`,
                    opacity: 0.5,
                  }}
                />
              )}

              {/* Checkmark for completed planets */}
              {isCompleted && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[8px] text-background-midnight font-bold">
                    ✓
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Step indicator text */}
      <div
        className="absolute text-center z-20"
        style={{ top: "calc(50% + 180px)" }}
      >
        <div className="flex items-center gap-3 justify-center">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className="transition-all duration-300"
              style={{
                width: i === currentStep - 1 ? "24px" : "8px",
                height: "8px",
                borderRadius: "4px",
                background:
                  i < currentStep
                    ? "#DAEC37"
                    : i === currentStep - 1
                      ? "#ABEBF7"
                      : "rgba(255, 255, 255, 0.2)",
                boxShadow:
                  i === currentStep - 1
                    ? "0 0 10px rgba(171, 235, 247, 0.5)"
                    : "none",
              }}
            />
          ))}
        </div>
        <p className="xe-overline text-text-lv3 mt-3">
          STEP {currentStep} OF {totalSteps}
        </p>
      </div>
    </div>
  );
};

// ============================================
// COSMIC INPUT FIELD
// ============================================
const CosmicInputField = ({
  type,
  name,
  value,
  onChange,
  error,
  handleNext,
  step,
  placeholder = "type here",
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleCustomChange = (e) => {
    let dataValue = e.target.value;
    if (name === "date_of_birth") {
      const parts = dataValue.split("-");
      if (parts.length > 0 && parts[0].length > 4) return;
      if (dataValue.length > 10) return;
    }
    onChange(e);
  };

  const isValidPassword = (text) => ({
    hasMinLength: text.length >= 8,
    hasLowercase: /[a-z]/.test(text),
    hasUppercase: /[A-Z]/.test(text),
    hasNumber: /[0-9]/.test(text),
    hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(text),
  });

  const passwordCriteria = isValidPassword(value);
  const allCriteriaValid =
    type === "password" &&
    Object.values(passwordCriteria).every((v) => v === true);

  return (
    <div className="w-full max-w-lg">
      <div className="relative group">
        {/* Glow effect */}
        <div
          className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: error
              ? "linear-gradient(135deg, rgba(255, 90, 95, 0.2), transparent)"
              : "linear-gradient(135deg, rgba(171, 235, 247, 0.15), transparent)",
            filter: "blur(8px)",
          }}
        />

        <input
          type={isPasswordVisible && type === "password" ? "text" : type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={handleCustomChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleNext();
            }
          }}
          aria-invalid={error ? true : undefined}
          className="relative w-full min-h-[60px] px-5 sm:px-6 py-4 pr-14 rounded-2xl text-white text-[20px] leading-7 font-medium transition-all duration-200 outline-none placeholder:text-white/40"
          style={{
            background: "rgba(25, 42, 77, 0.6)",
            border: error
              ? "2px solid rgba(255, 90, 95, 0.7)"
              : isFocused
                ? "2px solid rgba(171, 235, 247, 0.6)"
                : "2px solid rgba(171, 235, 247, 0.15)",
            boxShadow: error
              ? "0 0 30px rgba(255, 90, 95, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.03)"
              : isFocused
                ? "0 0 30px rgba(171, 235, 247, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.03)"
                : "inset 0 1px 0 rgba(255, 255, 255, 0.03)",
          }}
        />

        {/* Icons */}
        {type === "date" && (
          <Icons
            type="calendar"
            width={24}
            height={24}
            className="absolute right-5 top-1/2 -translate-y-1/2 text-white/50"
          />
        )}
        {type === "password" && (
          <button
            type="button"
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            className="xe-focus absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full opacity-70 hover:opacity-100 transition-opacity"
          >
            <Icons type={isPasswordVisible ? "invisible" : "visible"} />
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="xe-field-error mt-2 flex items-start gap-2">
          <Icons type="warning" className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </p>
      )}

      {/* Password criteria */}
      {type === "password" && (
        <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="text-text-lv2 text-base mb-3">password must contain:</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: "hasMinLength", label: "8+ characters" },
              { key: "hasLowercase", label: "lowercase letter" },
              { key: "hasUppercase", label: "uppercase letter" },
              { key: "hasNumber", label: "number (0-9)" },
              { key: "hasSymbol", label: "symbol (!@#$...)" },
            ].map((criteria) => (
              <div
                key={criteria.key}
                className="flex items-center gap-2 text-base transition-colors duration-200"
                style={{
                  color: passwordCriteria[criteria.key]
                    ? "#DAEC37"
                    : "#ffffff60",
                }}
              >
                <div
                  className="w-4 h-4 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{
                    background: passwordCriteria[criteria.key]
                      ? "rgba(218, 236, 55, 0.2)"
                      : "rgba(255, 255, 255, 0.1)",
                    border: passwordCriteria[criteria.key]
                      ? "1px solid #DAEC37"
                      : "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  {passwordCriteria[criteria.key] && (
                    <span className="text-[10px]">✓</span>
                  )}
                </div>
                {criteria.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next button */}
      <CosmicButton
        onClick={handleNext}
        step={step}
        className="mt-8"
        disabled={type === "password" && !allCriteriaValid && value.length > 0}
      />
    </div>
  );
};

// ============================================
// COSMIC GENDER SELECTION
// ============================================
const CosmicGenderSelection = ({
  value,
  onChange,
  error,
  handleNext,
  step,
}) => {
  const options = [
    { id: "m", label: "male", icon: "♂" },
    { id: "f", label: "female", icon: "♀" },
    { id: "n", label: "non-binary", icon: "⚧" },
    // Placeholder, not a symbol: a designer picks the mark, the hyphen holds the column.
    { id: "p", label: "prefer not to say", icon: "-" },
  ];

  return (
    <div className="w-full max-w-lg">
      <div className="space-y-3">
        {options.map((option) => {
          const isSelected = value === option.id;
          return (
            <label
              key={option.id}
              className="block cursor-pointer group"
              onClick={() =>
                onChange({ target: { name: "gender", value: option.id } })
              }
            >
              <div
                className="relative flex items-center gap-4 p-4 rounded-xl transition-all duration-300"
                style={{
                  background: isSelected
                    ? "rgba(171, 235, 247, 0.15)"
                    : "rgba(25, 42, 77, 0.4)",
                  border: isSelected
                    ? "2px solid rgba(171, 235, 247, 0.6)"
                    : "2px solid rgba(255, 255, 255, 0.08)",
                  boxShadow: isSelected
                    ? "0 0 30px rgba(171, 235, 247, 0.1)"
                    : "none",
                }}
              >
                {/* Orbital radio button */}
                <div
                  className="relative w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{
                    background: isSelected
                      ? "radial-gradient(circle at 30% 30%, #ABEBF7 0%, #6BC5D9 100%)"
                      : "rgba(255, 255, 255, 0.05)",
                    border: isSelected
                      ? "none"
                      : "2px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: isSelected
                      ? "0 0 15px rgba(171, 235, 247, 0.5)"
                      : "none",
                  }}
                >
                  {isSelected && (
                    <>
                      <div className="w-2.5 h-2.5 rounded-full bg-background-midnight" />
                      {/* Orbiting ring */}
                      <div className="absolute w-10 h-10 rounded-full border border-charlotte/30">
                        <div className="absolute w-1.5 h-1.5 rounded-full bg-charlotte left-1/2 -translate-x-1/2 -top-0.5" />
                      </div>
                    </>
                  )}
                </div>

                <span className="text-lg text-white/90">{option.label}</span>
              </div>
            </label>
          );
        })}
      </div>

      {error && (
        <p className="xe-field-error mt-2 flex items-start gap-2">
          <Icons type="warning" className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </p>
      )}

      <CosmicButton onClick={handleNext} step={step} className="mt-8" />
    </div>
  );
};

// ============================================
// COSMIC EDUCATION SELECTION
// ============================================
const CosmicEducationSelection = ({
  name,
  options,
  value,
  onChange,
  error,
  handleNext,
  step,
}) => {
  const [otherValue, setOtherValue] = useState("");

  if (!options?.data) return null;

  return (
    <div className="w-full max-w-lg">
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin">
        {options.data.map((item) => {
          const isSelected = Number(value) === Number(item.id);
          const isOther = item.name === "Other";

          return (
            <label
              key={item.id}
              className="block cursor-pointer group"
              onClick={() =>
                onChange({ target: { name, value: Number(item.id) } })
              }
            >
              <div
                className="relative flex items-center gap-4 p-4 rounded-xl transition-all duration-300"
                style={{
                  background: isSelected
                    ? "rgba(218, 236, 55, 0.15)"
                    : "rgba(25, 42, 77, 0.4)",
                  border: isSelected
                    ? "2px solid rgba(218, 236, 55, 0.6)"
                    : "2px solid rgba(255, 255, 255, 0.08)",
                  boxShadow: isSelected
                    ? "0 0 30px rgba(218, 236, 55, 0.1)"
                    : "none",
                }}
              >
                {/* Orbital radio */}
                <div
                  className="relative w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center flex-shrink-0"
                  style={{
                    background: isSelected
                      ? "radial-gradient(circle at 30% 30%, #DAEC37 0%, #B8C92E 100%)"
                      : "rgba(255, 255, 255, 0.05)",
                    border: isSelected
                      ? "none"
                      : "2px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: isSelected
                      ? "0 0 15px rgba(218, 236, 55, 0.5)"
                      : "none",
                  }}
                >
                  {isSelected && (
                    <div className="w-2.5 h-2.5 rounded-full bg-background-midnight" />
                  )}
                </div>

                <div className="flex-1 flex items-center gap-3">
                  <span className="text-lg text-white/90">{item.name}</span>
                  {isOther && isSelected && (
                    <input
                      type="text"
                      value={otherValue}
                      onChange={(e) => setOtherValue(e.target.value)}
                      placeholder="please specify"
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 bg-transparent border-b-2 border-pear/50 focus:border-pear outline-none text-white px-2 py-1"
                    />
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {error && (
        <p className="xe-field-error mt-2 flex items-start gap-2">
          <Icons type="warning" className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </p>
      )}

      <CosmicButton onClick={handleNext} step={step} className="mt-8" />
    </div>
  );
};

// ============================================
// COSMIC SELECT FIELD (School)
// ============================================
const CosmicSelectField = ({
  name,
  options,
  value,
  onChange,
  error,
  isChecked,
  setIsChecked,
  handleNext,
  step,
  setIsPrivacyOpen,
  isLoading,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(() => {
    if (!options?.data) return [];
    return options.data.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [options, searchTerm]);

  const selectedOption = options?.data?.find((item) => item.id === value);

  return (
    <div className="w-full max-w-lg">
      {/* Dropdown trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="relative cursor-pointer group"
      >
        <div
          className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 107, 157, 0.15), transparent)",
            filter: "blur(8px)",
          }}
        />
        <div
          className="relative flex items-center justify-between px-6 py-5 rounded-2xl text-white text-xl transition-all duration-300"
          style={{
            background: "rgba(25, 42, 77, 0.6)",
            border: isOpen
              ? "2px solid rgba(255, 107, 157, 0.6)"
              : "2px solid rgba(171, 235, 247, 0.15)",
          }}
        >
          <span className={selectedOption ? "text-white" : "text-white/30"}>
            {selectedOption?.name || "select your school..."}
          </span>
          <Icons
            type="downChevron"
            className={`w-5 h-5 text-white/50 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="mt-2 rounded-2xl overflow-hidden"
          style={{
            background: "rgba(13, 21, 38, 0.95)",
            border: "1px solid rgba(255, 107, 157, 0.3)",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* Search input */}
          <div className="p-3 border-b border-white/10">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="search schools..."
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30 outline-none focus:border-charlotte/50"
            />
          </div>

          {/* Options list */}
          <div className="max-h-[250px] overflow-y-auto scrollbar-thin">
            {filteredOptions.map((item) => (
              <div
                key={item.id}
                onClick={() => {
                  onChange({ target: { name, value: Number(item.id) } });
                  setIsOpen(false);
                  setSearchTerm("");
                }}
                className="px-6 py-3 cursor-pointer transition-colors hover:bg-white/10 text-white/80 hover:text-white"
              >
                {item.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Privacy checkbox */}
      <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => setIsChecked(!isChecked)}
            className="xe-focus relative w-6 h-6 rounded-md flex-shrink-0 transition-all duration-200 flex items-center justify-center mt-0.5"
            style={{
              background: isChecked
                ? "linear-gradient(135deg, #DAEC37 0%, #B8C92E 100%)"
                : "rgba(255, 255, 255, 0.05)",
              border: isChecked ? "none" : "2px solid rgba(255, 255, 255, 0.3)",
              boxShadow: isChecked
                ? "0 0 15px rgba(218, 236, 55, 0.4)"
                : "none",
            }}
          >
            {isChecked && (
              <span className="text-background-midnight text-sm font-bold">
                ✓
              </span>
            )}
          </button>
          <span
            onClick={() => setIsChecked(!isChecked)}
            className="text-text-lv2 text-md leading-6 cursor-pointer select-none"
          >
            I confirm that I have read and agree to the terms of thinccc's{" "}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsPrivacyOpen(true);
              }}
              className="xe-focus rounded-sm text-charlotte underline underline-offset-2 font-semibold hover:text-charlotte/80 transition-colors"
            >
              Privacy Policy
            </button>
          </span>
        </div>
      </div>

      {error && (
        <p className="xe-field-error mt-2 flex items-start gap-2">
          <Icons type="warning" className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </p>
      )}

      <CosmicButton
        onClick={handleNext}
        step={step}
        className="mt-8"
        isLoading={isLoading}
        disabled={!isChecked || !value}
      />
    </div>
  );
};

// ============================================
// COSMIC BUTTON
// ============================================
const CosmicButton = ({
  onClick,
  step,
  className = "",
  isLoading,
  disabled,
}) => {
  const buttonText =
    step === 1 ? "BEGIN JOURNEY" : step === 7 ? "LAUNCH" : "CONTINUE";

  /* F02: the shared action (the same pill as Mission and sign-in). Same
     handler, same disabled and pending rules; the shine and hover scale are
     gone, the pending label stays readable. */
  return (
    <ArrowFillButton
      onClick={onClick}
      disabled={disabled || isLoading}
      aria-busy={isLoading ? "true" : undefined}
      tone="dark"
      size="standard"
      arrow={!isLoading && step !== 7}
      className={`w-full ${className}`}
    >
      <span className="flex items-center justify-center gap-3">
        {isLoading ? (
          <>
            <svg
              className="animate-spin h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            LAUNCHING...
          </>
        ) : (
          <>
            {buttonText}
            {step === 7 && <span className="text-xl">🚀</span>}
          </>
        )}
      </span>
    </ArrowFillButton>
  );
};

// ============================================
// MAIN REGISTER COMPONENT
// ============================================
export const Register = () => {
  const router = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [, setCookies] = useCookies(["token", "oldPassword"]);
  const [step, setStep] = useState(1);
  const [isChecked, setIsChecked] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [educationOptions, setEducationOptions] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [error, setError] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [slideDirection, setSlideDirection] = useState("right");
  const [formData, setFormData] = useState({
    email: "",
    fullname: "",
    password: "",
    date_of_birth: "",
    gender: "m",
    education_id: 1,
    school_id: "",
    first_name: "",
    last_name: "",
  });

  const stepTitles = [
    "let's start with your email",
    "what should we call you?",
    "create a secure password",
    "when were you born?",
    "how do you identify?",
    "what's your education level?",
    "where do you study?",
  ];

  const stepSubtitles = [
    "we'll use this to keep your account secure",
    "enter your full name as it appears officially",
    "make it strong, make it memorable",
    "this helps us personalize your experience",
    "we celebrate all identities",
    "this helps us tailor your learning journey",
    "almost there! just one more step",
  ];

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
    fetchData("/education", setEducationOptions);
    fetchData("/schools", setSchoolOptions);
  }, []);

  const fetchData = (endpoint, setData) => {
    axiosInstance(endpoint, "get")
      .then((response) => setData(response.data))
      .catch((error) => console.log(error));
  };

  const handleNext = (e) => {
    const fields = [
      "email",
      "fullname",
      "password",
      "date_of_birth",
      "gender",
      "education_id",
      "school_id",
    ];
    const currentField = fields[step - 1];

    if (
      currentField &&
      validateFormField(formData[currentField], currentField) !== ""
    ) {
      setError((prev) => ({
        ...prev,
        [currentField]: validateFormField(formData[currentField], currentField),
      }));
      return;
    }

    if (!isChecked && step === 7) {
      setError({
        ...error,
        school_id: "Please read and agree to the terms",
      });
      return;
    }

    if (step < 7) {
      setSlideDirection("right");
      setStep(step + 1);
    } else {
      if (isLoading) return;
      handleSubmit(e);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setSlideDirection("left");
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const [firstName, lastName] = formData.fullname.split(" ");
    const updatedFormData = {
      ...formData,
      first_name: firstName,
      last_name: lastName || "",
    };

    const { fullname, ...dataToSend } = updatedFormData;

    try {
      setIsLoading(true);
      setIsLaunching(true);

      await axiosInstance("/users", "post", dataToSend);

      const loginResponse = await axiosInstance("/auth/login", "post", {
        email: formData.email,
        password: formData.password,
      });

      setCookies("oldPassword", formData.password, { maxAge: 3600 });
      setCookies("token", loginResponse.data.token, {
        maxAge: loginResponse.data.expires_in / 1000,
      });

      SuccessToast("Welcome aboard! Your journey begins now.");

      setTimeout(() => {
        setIsLoading(false);
        router("/season");
      }, 1500);
    } catch (error) {
      setIsLoading(false);
      setIsLaunching(false);
      setError({ email: error.response?.data?.message || "An error occurred" });
      setStep(1);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError((prev) => ({ ...prev, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]:
        name === "education_id" || name === "school_id" ? Number(value) : value,
    }));
  };

  const renderInputField = () => {
    const commonProps = {
      onChange: handleChange,
      handleNext,
      step,
    };

    switch (step) {
      case 1:
        return (
          <CosmicInputField
            type="email"
            name="email"
            value={formData.email}
            error={error.email}
            placeholder="your@email.com"
            {...commonProps}
          />
        );
      case 2:
        return (
          <CosmicInputField
            type="text"
            name="fullname"
            value={formData.fullname}
            error={error.fullname}
            placeholder="John Doe"
            {...commonProps}
          />
        );
      case 3:
        return (
          <CosmicInputField
            type="password"
            name="password"
            value={formData.password}
            error={error.password}
            placeholder="••••••••"
            {...commonProps}
          />
        );
      case 4:
        return (
          <CosmicInputField
            type="date"
            name="date_of_birth"
            value={formData.date_of_birth}
            error={error.date_of_birth}
            {...commonProps}
          />
        );
      case 5:
        return (
          <CosmicGenderSelection
            value={formData.gender}
            error={error.gender}
            {...commonProps}
          />
        );
      case 6:
        return (
          <CosmicEducationSelection
            name="education_id"
            options={educationOptions}
            value={formData.education_id}
            error={error.education_id}
            {...commonProps}
          />
        );
      case 7:
        return (
          <CosmicSelectField
            name="school_id"
            options={schoolOptions}
            value={formData.school_id}
            error={error.school_id}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            setIsPrivacyOpen={setIsPrivacyOpen}
            isLoading={isLoading}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#030D21]">
      {/* Deep space background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #030D21 0%, #0D1526 40%, #132039 70%, #0D1526 100%)",
        }}
      />

      {/* Cosmic elements */}
      <CosmicStarField isLaunching={isLaunching} />
      <NebulaEffects />

      {/* Launch flash overlay */}
      {isLaunching && (
        <div
          className="absolute inset-0 z-50 pointer-events-none"
          style={{
            animation: "successFlash 1500ms ease-out forwards",
          }}
        />
      )}

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex">
        {/* Left side - Form */}
        <div
          className="w-full lg:w-1/2 flex flex-col justify-center px-5 sm:px-8 md:px-16 lg:px-20 pt-10 pb-28 lg:py-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateX(0)" : "translateX(-30px)",
            transition: "all 700ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          {/* Back button */}
          <button
            type="button"
            onClick={step === 1 ? () => router("/login") : handleBack}
            className="xe-focus flex items-center gap-2 rounded-full pr-2 text-charlotte hover:text-charlotte/80 transition-colors mb-10 lg:mb-12 group w-fit"
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:-translate-x-1"
              style={{
                border: "2px solid rgba(171, 235, 247, 0.4)",
                background: "rgba(171, 235, 247, 0.05)",
              }}
            >
              <span className="text-lg">←</span>
            </div>
            <span className="text-base font-medium">
              {step === 1 ? "back to login" : "back"}
            </span>
          </button>

          {/* Step title */}
          <div
            key={step}
            style={{
              animation: `slideIn${slideDirection === "right" ? "Right" : "Left"} 400ms ease-out`,
            }}
          >
            <h1 className="text-text-lv0 text-xl md:text-3xl font-semibold mb-3">
              {stepTitles[step - 1]}
            </h1>
            <p className="xe-body text-text-lv3 mb-8 md:mb-10">
              {stepSubtitles[step - 1]}
            </p>

            {/* Form fields */}
            {renderInputField()}
          </div>
        </div>

        {/* Right side - Solar System Progress (hidden on mobile) */}
        <div
          className="hidden lg:flex w-1/2 items-center justify-center"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateX(0)" : "translateX(30px)",
            transition: "all 700ms cubic-bezier(0.16, 1, 0.3, 1) 200ms",
          }}
        >
          <SolarSystemProgress currentStep={step} totalSteps={7} />
        </div>
      </div>

      {/* Mobile progress indicator */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 p-4 z-20"
        style={{
          background:
            "linear-gradient(to top, rgba(3, 13, 33, 0.95), transparent)",
        }}
      >
        <div className="flex items-center justify-center gap-2">
          {Array.from({ length: 7 }, (_, i) => (
            <div
              key={i}
              className="transition-all duration-300"
              style={{
                width: i === step - 1 ? "24px" : "8px",
                height: "8px",
                borderRadius: "4px",
                background:
                  i < step
                    ? "#DAEC37"
                    : i === step - 1
                      ? "#ABEBF7"
                      : "rgba(255, 255, 255, 0.2)",
              }}
            />
          ))}
        </div>
        <p className="text-center text-text-lv3 text-sm mt-2">
          Step {step} of 7
        </p>
      </div>

      <Privacy isOpen={isPrivacyOpen} setIsOpen={setIsPrivacyOpen} />

      {/* Animation styles */}
      <style>{`
        @keyframes registerStarTwinkle {
          0%, 100% { opacity: var(--star-opacity, 0.5); transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        @keyframes nebulaDrift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -20px) scale(1.05); }
        }

        @keyframes nebulaDrift2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-20px, 30px) scale(1.08); }
        }

        @keyframes orbit {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes successFlash {
          0% { background: transparent; }
          30% { background: rgba(171, 235, 247, 0.3); }
          100% { background: rgba(255, 255, 255, 1); }
        }

        @keyframes warpSpeed {
          0% { transform: scale(1); opacity: var(--star-opacity, 0.5); }
          100% { transform: scale(20) translateY(-50vh); opacity: 0; }
        }

        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 2px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(171, 235, 247, 0.3);
          border-radius: 2px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(171, 235, 247, 0.5);
        }
      `}</style>
    </div>
  );
};
